import React, { useEffect, useState } from 'react';
import { SVGShape } from '../../types';

const svgCache = new Map<string, string>();

export const SVGShapeRenderer: React.FC<{ shape: SVGShape }> = ({ shape }) => {
  const [svgContent, setSvgContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const opacity = shape.hidden ? 0 : 1;
  const opacityAttr = opacity !== 1 ? { opacity } : {};

  useEffect(() => {
    const loadSVG = async () => {
      // If we already have svgContent, use it
      if (shape.svgContent) {
        setSvgContent(shape.svgContent);
        return;
      }

      // If we have a path URL, fetch it
      if (shape.path && !svgCache.has(shape.path)) {
        setIsLoading(true);
        try {
          let svgText = '';

          if (shape.path.startsWith('https://cdn.gettactic.com')) {
            // Handle CDN URLs
            const response = await fetch(shape.path);
            svgText = await response.text();
          } else if (shape.path.startsWith('data:')) {
            // Handle data URLs
            svgText = decodeURIComponent(shape.path.split(',')[1]);
          } else {
            // Handle other URLs
            const response = await fetch(shape.path);
            svgText = await response.text();
          }

          svgCache.set(shape.path, svgText);
          setSvgContent(svgText);
        } catch (error) {
          console.error('Error loading SVG:', error);
        } finally {
          setIsLoading(false);
        }
      } else if (svgCache.has(shape.path)) {
        // Use cached content
        setSvgContent(svgCache.get(shape.path)!);
      }
    };

    loadSVG();
  }, [shape.path, shape.svgContent]);

  const scaleX = shape.scaleX || 1;
  const scaleY = shape.scaleY || 1;
  const transform = `translate(${shape.x}, ${shape.y}) scale(${scaleX}, ${scaleY})`;

  if (isLoading) {
    // Show a loading placeholder
    return (
      <rect
        x={shape.x}
        y={shape.y}
        width={shape.width}
        height={shape.height}
        fill="rgba(0, 0, 255, 0.3)"
        stroke="blue"
        strokeWidth={1}
        {...opacityAttr}
      />
    );
  }

  if (svgContent) {
    // Render the SVG content
    return (
      <g transform={transform} {...opacityAttr}>
        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation> */}
        <g dangerouslySetInnerHTML={{ __html: svgContent }} />
      </g>
    );
  }

  if (
    shape.path &&
    !shape.path.startsWith('http') &&
    !shape.path.startsWith('data:')
  ) {
    // Fallback to path if it's a simple path (not a URL)
    return (
      <g transform={transform} {...opacityAttr}>
        <path
          d={shape.path}
          fill={shape.color || 'none'}
          stroke={shape.stroke || 'black'}
          strokeWidth={shape.strokeWidth || 1}
        />
      </g>
    );
  }

  // Fallback placeholder
  return (
    <rect
      x={shape.x}
      y={shape.y}
      width={shape.width}
      height={shape.height}
      fill="rgba(255, 0, 0, 0.3)"
      stroke="red"
      strokeWidth={2}
      {...opacityAttr}
    />
  );
};
